import { useCallback } from 'react';

import {
	ufoEventProvisioningReadinessLatency,
	ufoEventE2eProvisioningLatency,
	ufoEventE2eProvisioningSystemLatency,
	ufoEventProductSetupSystemLatency,
	ufoEventProductSetupLatency,
} from '@/components/signup/utils/ufo-events';
import { SignupUfoCustomData } from '@/components/signup/utils/ufo.types';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags as gates } from '@/utils/feature-flags/feature-flag-manifest';

type FinalProvisioningScreen = NonNullable<SignupUfoCustomData['finalProvisioningScreen']>;

/**
 * The combined system metric counts the union of instrumented readiness and unpaused setup
 * intervals. Gaps between those intervals, including render scheduling and redirect-only
 * delays, are excluded; this is not an exhaustive measurement of all system time.
 */
export const useProvisioningLatencyUfo = () => {
	const isProvisioningLatencyUfoEnabled = checkFeatureFlagGate(
		gates.wacUfoEventProvisioningLatency,
	);
	const isSystemLatencyUfoEnabled = checkFeatureFlagGate(
		gates.wacUfoEventProvisioningBackendLatency,
	);

	const startE2eProvisioning = useCallback(
		(options: Parameters<typeof ufoEventE2eProvisioningLatency.start>[0]) => {
			if (isProvisioningLatencyUfoEnabled) {
				void ufoEventE2eProvisioningLatency.start(options);
			}
		},
		[isProvisioningLatencyUfoEnabled],
	);

	const startProductSetup = useCallback(
		(options: Parameters<typeof ufoEventProductSetupLatency.start>[0]) => {
			if (isProvisioningLatencyUfoEnabled) {
				void ufoEventProductSetupLatency.start(options);
			}
		},
		[isProvisioningLatencyUfoEnabled],
	);

	const completeProductSetup = useCallback(
		(finalProvisioningScreen: FinalProvisioningScreen) => {
			if (isProvisioningLatencyUfoEnabled) {
				void ufoEventProductSetupLatency.success({ customData: { finalProvisioningScreen } });
			}
		},
		[isProvisioningLatencyUfoEnabled],
	);

	const abortE2eProvisioningSystem = useCallback(
		(finalProvisioningScreen?: FinalProvisioningScreen) => {
			if (isSystemLatencyUfoEnabled && ufoEventE2eProvisioningSystemLatency.isRunning()) {
				void ufoEventE2eProvisioningSystemLatency.abort({
					customData: { finalProvisioningScreen },
				});
			}
		},
		[isSystemLatencyUfoEnabled],
	);

	const completeE2eProvisioning = useCallback(
		(finalProvisioningScreen?: FinalProvisioningScreen) => {
			if (isProvisioningLatencyUfoEnabled) {
				void ufoEventE2eProvisioningLatency.success({ customData: { finalProvisioningScreen } });
			}
			// This is a redirect boundary even when a product's setup is still active.
			// The finishing state prevents a later setup callback from emitting twice.
			if (isSystemLatencyUfoEnabled && ufoEventE2eProvisioningSystemLatency.isRunning()) {
				if (ufoEventProvisioningReadinessLatency.isRunning()) {
					abortE2eProvisioningSystem(finalProvisioningScreen);
				} else {
					void ufoEventE2eProvisioningSystemLatency.success({
						customData: { finalProvisioningScreen },
					});
				}
			}
		},
		[isProvisioningLatencyUfoEnabled, isSystemLatencyUfoEnabled, abortE2eProvisioningSystem],
	);

	const completeAll = useCallback(
		(finalProvisioningScreen: FinalProvisioningScreen) => {
			completeProductSetup(finalProvisioningScreen);
			completeE2eProvisioning(finalProvisioningScreen);
		},
		[completeProductSetup, completeE2eProvisioning],
	);

	const startProvisioningReadiness = useCallback(
		(options: Parameters<typeof ufoEventProvisioningReadinessLatency.start>[0]) => {
			if (isSystemLatencyUfoEnabled) {
				void ufoEventProvisioningReadinessLatency.start(options);
				void ufoEventE2eProvisioningSystemLatency.start(options);
			}
		},
		[isSystemLatencyUfoEnabled],
	);

	const completeProvisioningReadiness = useCallback(
		(finalProvisioningScreen?: FinalProvisioningScreen) => {
			if (isSystemLatencyUfoEnabled) {
				void ufoEventProvisioningReadinessLatency.success({
					customData: { finalProvisioningScreen },
				});
				// Questions may continue after provisioning, before automated setup begins.
				// An animation override can already have started setup; keep counting it.
				if (
					!ufoEventProductSetupSystemLatency.isRunning() ||
					ufoEventProductSetupSystemLatency.isPaused()
				) {
					ufoEventE2eProvisioningSystemLatency.pause();
				}
			}
		},
		[isSystemLatencyUfoEnabled],
	);

	const startProductSetupSystem = useCallback(
		(options: Parameters<typeof ufoEventProductSetupSystemLatency.start>[0]) => {
			if (isSystemLatencyUfoEnabled) {
				const wasSetupPaused =
					ufoEventProductSetupSystemLatency.isRunning() &&
					ufoEventProductSetupSystemLatency.isPaused();
				void ufoEventProductSetupSystemLatency.start(options);
				// A repeated start must preserve a pause for user input, just as UFO does.
				if (!wasSetupPaused) {
					ufoEventE2eProvisioningSystemLatency.resume();
				}
			}
		},
		[isSystemLatencyUfoEnabled],
	);

	const pauseProductSetupSystem = useCallback(() => {
		if (isSystemLatencyUfoEnabled) {
			ufoEventProductSetupSystemLatency.pause();
			// Provisioning still counts when it overlaps input on a setup screen.
			if (!ufoEventProvisioningReadinessLatency.isRunning()) {
				ufoEventE2eProvisioningSystemLatency.pause();
			}
		}
	}, [isSystemLatencyUfoEnabled]);

	const resumeProductSetupSystem = useCallback(
		(options: Parameters<typeof ufoEventProductSetupSystemLatency.start>[0]) => {
			if (isSystemLatencyUfoEnabled) {
				ufoEventProductSetupSystemLatency.resume();
				// Start a measurement if none is active.
				// UFO ignores this call when a measurement is already running.
				void ufoEventProductSetupSystemLatency.start(options);
				ufoEventE2eProvisioningSystemLatency.resume();
			}
		},
		[isSystemLatencyUfoEnabled],
	);

	const completeProductSetupSystem = useCallback(
		(finalProvisioningScreen: FinalProvisioningScreen) => {
			if (isSystemLatencyUfoEnabled) {
				void ufoEventProductSetupSystemLatency.success({ customData: { finalProvisioningScreen } });
				if (
					!ufoEventProvisioningReadinessLatency.isRunning() &&
					ufoEventE2eProvisioningSystemLatency.isRunning()
				) {
					void ufoEventE2eProvisioningSystemLatency.success({
						customData: { finalProvisioningScreen },
					});
				}
			}
		},
		[isSystemLatencyUfoEnabled],
	);

	return {
		startE2eProvisioning,
		startProductSetup,
		completeProductSetup,
		completeE2eProvisioning,
		abortE2eProvisioningSystem,
		completeAll,
		isProvisioningLatencyUfoEnabled,
		startProvisioningReadiness,
		completeProvisioningReadiness,
		startProductSetupSystem,
		pauseProductSetupSystem,
		resumeProductSetupSystem,
		completeProductSetupSystem,
	};
};
